<template>
  <div class="flex h-full w-full flex-col gap-2 px-8 py-6">
    <p
      v-if="!number"
      class="message flex h-full min-h-fit items-center justify-center p-4 text-xl leading-8 text-gray-500"
    >
      {{ $t('call_center.select_a_call_to_see_history') }}
    </p>

    <template v-else>
      <call-log-header />
      <div class="grow overflow-y-auto">
        <div class="flex min-h-full flex-col gap-2 pe-1">
          <call-log-item
            v-for="item in history"
            :key="item.id"
            :call-info="item"
          />
          <div
            v-if="!history.length && !isFetching"
            class="text-center"
          >
            {{ $t('call_center.no_call_log_is_existing_for_selected_contact') }}
          </div>

          <div
            v-if="isFetching"
            class="flex h-full grow items-center rounded-xl bg-white p-6"
          >
            <beva-loading />
          </div>

          <div
            v-else-if="nextPage > 1 && nextPage <= meta.last_page"
            v-observe-visibility="loadMore"
            class="h-2"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { type CallLog } from '@bv/api';
  import { useSipStore } from '@bv/stores';
  import { storeToRefs } from 'pinia';
  import { ref, computed, watch } from 'vue';
  import { useRoute } from 'vue-router';

  import callLogsApi from '@/api/webphone/callLogs.api';

  import CallLogHeader from './CallLogHeader.vue';
  import CallLogItem from './CallLogItem.vue';

  const sipStore = useSipStore();
  const route = useRoute();

  const nextPage = ref(1);
  const history = ref<any[]>([]);
  const meta = ref<any>({});
  const isFetching = ref(false);

  const { recentlyAddedLog } = storeToRefs(sipStore);

  const number = computed(() => {
    return route.params.number;
  });

  /**
   * Resets pagination and fetches the first page of history.
   */
  function refetchHistory() {
    nextPage.value = 1;
    fetchNextPage();
  }

  /**
   * Fetches the call log history for the current page.
   */
  async function fetchNextPage() {
    if (nextPage.value === 1) {
      meta.value = {};
      history.value = [];
    }

    if (!number.value) {
      return;
    }

    isFetching.value = true;
    callLogsApi
      .get(
        {
          params: {
            'filter[phone_number]': number.value,
            page: nextPage.value,
          },
        },
        'logs',
      )
      .then((response) => {
        history.value.push(...response.data.data);
        meta.value = response.data.meta;
        nextPage.value = meta.value.current_page + 1;
      })
      .finally(() => {
        isFetching.value = false;
      });
  }

  /**
   * Triggered by an observer to load more history when the user scrolls.
   * @param {boolean} isVisible - Whether the trigger element is visible.
   */
  function loadMore(isVisible: boolean) {
    if (isVisible && !isFetching.value) {
      fetchNextPage();
    }
  }

  watch(
    number,
    () => {
      refetchHistory();
    },
    { immediate: true },
  );

  watch(
    recentlyAddedLog,
    (log: CallLog | undefined) => {
      if (log?.phone_number === number.value) {
        refetchHistory();
      }
    },
    { deep: true },
  );
</script>
